import { useEffect, useRef } from "react";
import { ApiAIPromptStatus, ApiSimpleComponent } from "@incendium/api";
import { readComponent } from "../services/componentService";
import produce from "immer";

interface UsePollPendingComponentsOptions {
  projectId: number | undefined;
  components: ApiSimpleComponent | ApiSimpleComponent[] | null | undefined;
  onStatusChange?: (component: ApiSimpleComponent) => void;
  setComponents?: (
    updater: (components: ApiSimpleComponent[]) => ApiSimpleComponent[],
  ) => void;
  interval?: number;
  enabled?: boolean;
}

export const usePollPendingComponents = ({
  projectId,
  components,
  onStatusChange,
  setComponents,
  interval = 5000,
  enabled = true,
}: UsePollPendingComponentsOptions) => {
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled || !projectId) {
      return;
    }

    // Normalize components to array
    const componentsArray: ApiSimpleComponent[] = Array.isArray(components)
      ? components
      : components
        ? [components]
        : [];

    // Filter to only pending components
    const pendingComponents = componentsArray.filter(
      (c) => c.prompt?.status === ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING,
    );

    if (pendingComponents.length === 0) {
      return;
    }

    const pollComponents = async () => {
      for (const pendingComponent of pendingComponents) {
        if (!pendingComponent.id) continue;

        try {
          const res = await readComponent(projectId, pendingComponent.id);

          // Check if status changed from pending
          if (
            res.component?.prompt?.status !==
            ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING
          ) {
            const updatedComponent = res.component as ApiSimpleComponent;

            if (onStatusChange) {
              onStatusChange(updatedComponent);
            }

            if (setComponents && updatedComponent) {
              setComponents((currentComponents) =>
                produce(currentComponents, (draft) => {
                  const idx = draft.findIndex(
                    (d) => d.id === updatedComponent.id,
                  );
                  if (idx >= 0) {
                    draft[idx] = { ...draft[idx], ...updatedComponent };
                  }
                }),
              );
            }
          }
        } catch (error) {
          console.error("Error polling component:", error);
        }
      }
    };

    // Start polling
    pollingRef.current = setInterval(pollComponents, interval);

    // Cleanup on unmount or dependency change
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [projectId, components, onStatusChange, setComponents, interval, enabled]);
};
